import { useState, createContext, useContext, useCallback, useMemo, useEffect } from "react";

/**
 * CompareProvider — global "compare bin" that lets the user mark up to 3
 * customizable products and view them side-by-side in a comparison modal.
 *
 * State is persisted to localStorage so the selection survives navigation.
 */
const KEY = "tc.compare.v1";
const MAX = 3;

const Ctx = createContext(null);

export function CompareProvider({ children }) {
  const [ids, setIds] = useState([]);
  // Don't persist before the initial load completes, otherwise the first render
  // (with ids=[]) would overwrite a previously stored selection.
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setIds(parsed.slice(0, MAX));
      }
    } catch { /* ignore */ }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try { localStorage.setItem(KEY, JSON.stringify(ids)); } catch { /* ignore */ }
  }, [ids, hydrated]);

  const toggle = useCallback((productId) => {
    setIds((prev) => {
      if (prev.includes(productId)) return prev.filter((p) => p !== productId);
      if (prev.length >= MAX) return prev;
      return [...prev, productId];
    });
  }, []);
  const remove = useCallback((productId) => setIds((p) => p.filter((id) => id !== productId)), []);
  const clear  = useCallback(() => setIds([]), []);
  const has    = useCallback((productId) => ids.includes(productId), [ids]);

  const value = useMemo(() => ({
    ids, count: ids.length, max: MAX,
    full: ids.length >= MAX,
    toggle, remove, clear, has,
  }), [ids, toggle, remove, clear, has]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCompare() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useCompare must be used inside <CompareProvider>");
  return v;
}
